import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import classes from "./Chat.module.css";
import TaskTracker from "./TaskTracker";
import API_BASE_URL from "../config";

// Layout & Widgets
import Header from "./Header";
import Sidebar from "./Sidebar";
import ResearchPlan from "./ResearchPlan";
import Outline from "./Outline";
import Preview from "./Preview";
import Download from "./Download";
import LoginPopup from "./LoginPopup";

const Chat = () => {
    const navigate = useNavigate();
    const { sessionId } = useParams();

    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [sessionData, setSessionData] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    // NEW: Controls the floatable Sidebar
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const [initialLoading, setInitialLoading] = useState(!!sessionId);
    const [isChangingSession, setIsChangingSession] = useState(false);
    const [currentUser, setCurrentUser] = useState(() => {
        const saved = localStorage.getItem("chitra_user");
        return saved ? JSON.parse(saved) : null;
    });
    const [showLoginPopup, setShowLoginPopup] = useState(false);
    const [pendingSubmission, setPendingSubmission] = useState(null);
    const status = sessionData?.pipeline_status;
    // const isScoping = !sessionId ||
    //     (initialLoading && !sessionData) ||
    //     (!status && !initialLoading) ||
    //     ["SCOPING", "INITIALISED", "INGESTING"].includes(status);
    const isScoping = !sessionId ||
        (initialLoading && !sessionData && !isChangingSession) || // Updated check here
        (!status && !initialLoading) ||
        ["SCOPING", "INITIALISED", "INGESTING"].includes(status);

    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);

    // --- Logout Handler ---
    const handleLogout = () => {
        localStorage.removeItem("chitra_user");
        setCurrentUser(null);
        navigate("/");
    };

    useEffect(() => {
        if (sessionData?.pipeline_status === "AWAITING_HUMAN" && sessionData?.diplomat_checkpoint) {
            const cp = sessionData.diplomat_checkpoint;

            if (!cp.resolved) {
                const msgId = `sys-${cp.checkpoint_id}`;

                setMessages((prev) => {
                    if (prev.some((m) => m.id === msgId)) return prev;

                    let text = "I've paused the pipeline for your review.";

                    if (cp.checkpoint_type === "CP-2") {
                        text = "✅ I've drafted a Research Plan! Please review the queries on the right. Let me know if you approve or if you'd like me to change the focus.";
                    } else if (cp.checkpoint_type === "CP-3") {
                        text = "✅ The Slide Outline is ready! Check out the structure on the right. Say 'Looks good' to start generating the actual slides.";
                    } else if (cp.checkpoint_type === "CP-4") {
                        text = "🎨 The presentation draft is ready! Review the designs on the right. You can ask me to redesign specific elements, or just say 'Assemble it' to finish your PPTX.";
                    }

                    return [...prev, { id: msgId, role: "ai", content: text }];
                });
            }
        }
    }, [sessionData?.diplomat_checkpoint?.checkpoint_id, sessionData?.diplomat_checkpoint?.resolved, sessionData?.pipeline_status]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // useEffect(() => {
    //     if (!sessionId) {
    //         setMessages([
    //             { id: 1, role: "ai", content: "Hi! I am your AI Presentation Team. Give me a topic or upload a document, and I'll build a deck for you." }
    //         ]);
    //         setSessionData(null);
    //         return;
    //     }

    //     let isMounted = true;

    //     const fetchInitialState = async () => {
    //         try {
    //             const response = await fetch(`${API_BASE_URL}/api/v1/sessions/${sessionId}`);
    //             if (!response.ok || !isMounted) {
    //                 setInitialLoading(false);
    //                 return;
    //             }
    //             const data = await response.json();
    //             const state = data.state || data;

    //             setSessionData(state);
    //             setInitialLoading(false);

    //             const historyRes = await fetch(`${API_BASE_URL}/api/v1/chat/${sessionId}/history`);
    //             if (historyRes.ok && isMounted) {
    //                 const historyData = await historyRes.json();

    //                 if (historyData.history && historyData.history.length > 0) {
    //                     const cleanHistory = historyData.history.filter(msg =>
    //                         (msg.role === "user" || msg.role === "ai") &&
    //                         msg.content &&
    //                         String(msg.content).trim() !== ""
    //                     );

    //                     setMessages((prev) => {
    //                         if (prev.length > cleanHistory.length) {
    //                             return prev;
    //                         }
    //                         return cleanHistory.length > 0 ? cleanHistory : prev;
    //                     });
    //                 } else {
    //                     setMessages((prev) => {
    //                         if (prev.length > 1) return prev;
    //                         return [{ id: 1, role: "ai", content: "Hi! I am your AI Presentation Team. Give me a topic or upload a document, and I'll build a deck for you." }];
    //                     });
    //                 }
    //             }
    //         } catch (err) {
    //             console.error("Initial load error:", err);
    //             setInitialLoading(false);
    //         }
    //     };

    //     fetchInitialState();

    //     return () => { isMounted = false; };
    // }, [sessionId]);

    useEffect(() => {
        if (!sessionId) {
            setMessages([
                { id: 1, role: "ai", content: "Hi! I am your AI Presentation Team. Give me a topic or upload a document, and I'll build a deck for you." }
            ]);
            setSessionData(null);
            setInitialLoading(false);
            setIsChangingSession(false);
            return;
        }

        let isMounted = true;

        // ─── RESET AND CONFIGURE WORKSPACE TRANSIT ON ROUTE CHANGE ───
        setIsChangingSession(true);
        setInitialLoading(true);
        setSessionData(null);
        setMessages([]);

        const fetchInitialState = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/v1/sessions/${sessionId}`);
                if (!response.ok || !isMounted) {
                    setInitialLoading(false);
                    setIsChangingSession(false);
                    return;
                }
                const data = await response.json();
                const state = data.state || data;

                if (isMounted) {
                    setSessionData(state);
                    setInitialLoading(false);
                    setIsChangingSession(false); // Data is loaded, release layout lock
                }

                const historyRes = await fetch(`${API_BASE_URL}/api/v1/chat/${sessionId}/history`);
                if (historyRes.ok && isMounted) {
                    const historyData = await historyRes.json();

                    if (historyData.history && historyData.history.length > 0) {
                        const cleanHistory = historyData.history.filter(msg =>
                            (msg.role === "user" || msg.role === "ai") &&
                            msg.content &&
                            String(msg.content).trim() !== ""
                        );
                        setMessages(cleanHistory);
                    } else {
                        setMessages([
                            { id: 1, role: "ai", content: "Hi! I am your AI Presentation Team. Give me a topic or upload a document, and I'll build a deck for you." }
                        ]);
                    }
                }
            } catch (err) {
                console.error("Initial load error:", err);
                if (isMounted) {
                    setInitialLoading(false);
                    setIsChangingSession(false);
                }
            }
        };

        fetchInitialState();

        return () => { isMounted = false; };
    }, [sessionId]);

    useEffect(() => {
        if (!sessionId) return;
        let isMounted = true;

        const intervalId = setInterval(async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/v1/sessions/${sessionId}`);
                if (!response.ok || !isMounted) return;

                const data = await response.json();
                const state = data.state || data;

                setSessionData(state);

                if (state.pipeline_status === "DONE" || state.pipeline_status === "ERROR") {
                    clearInterval(intervalId);
                }
            } catch (err) {
                console.error("Polling error:", err);
            }
        }, 3000);

        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };
    }, [sessionId]);

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) setSelectedFile(e.target.files[0]);
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!inputValue.trim() && !selectedFile) return;

        if (!currentUser) {
            setPendingSubmission({ text: inputValue, file: selectedFile });
            setShowLoginPopup(true);
            return;
        }

        await processSubmission(inputValue, selectedFile, currentUser.user_id);
    };

    const processSubmission = async (textInput, fileInput, userId) => {
        const userContent = fileInput ? `Uploaded Document: ${fileInput.name}\n${textInput}` : textInput;

        setMessages(prev => [...prev, { id: Date.now(), role: "user", content: userContent }]);
        setIsProcessing(true);
        setInputValue("");
        setSelectedFile(null);

        try {
            let activeSessionId = sessionId;

            if (!activeSessionId) {
                const formData = new FormData();
                if (fileInput) {
                    formData.append("input_type", "document");
                    formData.append("file", fileInput);
                } else {
                    formData.append("input_type", "topic");
                }
                formData.append("topic", textInput);
                formData.append("user_id", userId);

                const sessionRes = await fetch(`${API_BASE_URL}/api/v1/sessions`, {
                    method: "POST",
                    body: formData,
                });
                const sessionPayload = await sessionRes.json();
                activeSessionId = sessionPayload.session_id;

                window.dispatchEvent(new CustomEvent('new_presentation_started', { detail: { session_id: activeSessionId, title: textInput || (fileInput ? fileInput.name : "Untitled Presentation"), created_at: new Date().toISOString(), pipeline_status: "INITIALISED" } }));

                navigate(`/chat/${activeSessionId}`);
            }

            const chatRes = await fetch(`${API_BASE_URL}/api/v1/chat/${activeSessionId}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ message: userContent })
            });
            const chatData = await chatRes.json();
            setMessages(prev => [...prev, { id: Date.now(), role: "ai", content: chatData.reply || "Got it!" }]);

        } catch (err) {
            setMessages(prev => [...prev, { id: Date.now(), role: "ai", content: `Error: ${err.message}` }]);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleLoginSuccess = async (userData) => {
        localStorage.setItem("chitra_user", JSON.stringify(userData));
        setCurrentUser(userData);
        setShowLoginPopup(false);

        if (pendingSubmission) {
            await processSubmission(pendingSubmission.text, pendingSubmission.file, userData.user_id);
            setPendingSubmission(null);
        }
    };

    const renderRightPanel = () => {
        if (!sessionData) {
            return (
                <div className={classes.canvasPlaceholder}>
                    <h1>AI Workspace</h1>
                    <p>Tell me what you want to build in the chat to get started!</p>
                </div>
            );
        }

        const status = sessionData.pipeline_status;
        const cp = sessionData.diplomat_checkpoint?.checkpoint_type;

        if (status === "ERROR") {
            return (
                <div className={classes.canvasPlaceholder}>
                    <h1 style={{ color: "#ef4444" }}>Pipeline Error</h1>
                    <p style={{ color: "#7f1d1d", background: "#fef2f2", padding: "15px", borderRadius: "8px" }}>
                        {sessionData.error_message || "An unknown agent error occurred."}
                    </p>
                </div>
            );
        }

        if (status === "AWAITING_HUMAN") {
            if (cp === "CP-2") return <ResearchPlan sessionData={sessionData} />;
            if (cp === "CP-3") return <Outline sessionData={sessionData} />;
            if (cp === "CP-4") return <Preview sessionData={sessionData} />;
        }

        if (["WRITING", "QA_CHECKING", "ASSEMBLING", "DONE"].includes(status)) {
            return <Preview sessionData={sessionData} />;
        }

        if (["RESEARCH_PLANNING", "RESEARCHING"].includes(status)) {
            return (
                <div className={classes.canvasPlaceholder}>
                    <h1>Working...</h1>
                    <p>The AI agents are currently executing: <b>{status.replace("_", " ")}</b></p>
                </div>
            );
        }

        return (
            <div className={classes.canvasPlaceholder}>
                <h1>Scoping Presentation</h1>
                <p>Chat with the AI to finalize your requirements.</p>
            </div>
        );
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100vh", width: "100vw", overflow: "hidden" }}>

            {/* Global Header (Now controls Sidebar toggling) */}
            <Header
                user={currentUser}
                onLogout={handleLogout}
                toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
                onLoginClick={() => setShowLoginPopup(true)}
            />

            <div style={{ display: "flex", flex: 1, overflow: "hidden", position: "relative" }}>

                {/* Global Floating Sidebar */}
                {currentUser && (
                    <Sidebar
                        user={currentUser}
                        currentSessionId={sessionId}
                        isOpen={isSidebarOpen}
                        onClose={() => setIsSidebarOpen(false)}
                    />
                )}

                {/* Original App Layout Container */}
                <div style={{ flex: 1, display: "flex", position: "relative", overflow: "hidden" }}>
                    <div className={`${classes.appLayout} ${isScoping ? classes.appLayoutCentered : ""}`} style={{ width: "100%", height: "100%" }}>

                        <div className={`${classes.chatPanel} ${isScoping ? classes.chatPanelCentered : ""}`}>

                            {!currentUser && (
                                <div className={classes.chatHeader}>
                                    <h2>CHITRA -Slidegen</h2>
                                </div>
                            )}

                            {sessionData && !isScoping && (
                                <div className={classes.trackerContainer}>
                                    <TaskTracker
                                        currentStatus={sessionData.pipeline_status}
                                        checkpoint={sessionData.diplomat_checkpoint?.checkpoint_type}
                                    />
                                </div>
                            )}

                            <div className={classes.messageFeed}>
                                {messages
                                    .filter(msg => (msg.role === "user" || msg.role === "ai") && msg.content && String(msg.content).trim() !== "")
                                    .map((msg) => (
                                        <div key={msg.id} className={`${classes.messageWrapper} ${msg.role === "user" ? classes.userWrapper : classes.aiWrapper}`}>
                                            <div className={`${classes.messageBubble} ${msg.role === "user" ? classes.userBubble : classes.aiBubble}`}>
                                                <span style={{ whiteSpace: "pre-wrap" }}>{msg.content}</span>
                                            </div>
                                        </div>
                                    ))}

                                {isProcessing && (
                                    <div className={`${classes.messageWrapper} ${classes.aiWrapper}`}>
                                        <div className={`${classes.messageBubble} ${classes.aiBubble} ${classes.typingIndicator}`}>
                                            <span>.</span><span>.</span><span>.</span>
                                        </div>
                                    </div>
                                )}

                                {status === "DONE" && (
                                    <div className={`${classes.messageWrapper} ${classes.aiWrapper}`}>
                                        <div className={`${classes.messageBubble} ${classes.aiBubble}`} style={{ width: "100%", marginTop: "10px" }}>
                                            <h3 style={{ fontSize: "1.1rem", marginBottom: "15px", color: "#1e293b" }}>
                                                🎉 Presentation Ready!
                                            </h3>
                                            <Download sessionData={sessionData} />
                                            {sessionData.inspector_score && (
                                                <div style={{ marginTop: "15px", fontSize: "0.9rem", color: "#64748b", borderTop: "1px solid #e2e8f0", paddingTop: "10px" }}>
                                                    <strong>Fact-Check Grounding Score:</strong> {Math.round(sessionData.inspector_score * 100)}%
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div ref={messagesEndRef} />
                            </div>

                            <div className={classes.inputArea}>
                                {status === "DONE" ? (
                                    <div className={classes.inputForm} style={{ justifyContent: "center", background: "transparent", boxShadow: "none" }}>
                                        <button
                                            type="button"
                                            className={classes.sendBtn}
                                            style={{ width: "auto", padding: "12px 24px", borderRadius: "12px", background: "linear-gradient(135deg, #10b981, #059669)", fontWeight: "bold" }}
                                            onClick={() => {
                                                navigate("/chat");
                                            }}
                                        >
                                            + Start a New Presentation
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        {selectedFile && (
                                            <div className={classes.fileAttachmentPreview}>
                                                📄 {selectedFile.name} <button onClick={() => setSelectedFile(null)}>✕</button>
                                            </div>
                                        )}
                                        <form onSubmit={handleSendMessage} className={classes.inputForm}>
                                            <button type="button" className={classes.attachBtn} onClick={() => fileInputRef.current.click()}>📎</button>
                                            <input type="file" ref={fileInputRef} style={{ display: "none" }} accept=".pdf,.docx" onChange={handleFileChange} />
                                            <input type="text" placeholder="Tell me what to build..." value={inputValue} onChange={(e) => setInputValue(e.target.value)} className={classes.textInput} disabled={isProcessing} />
                                            <button type="submit" className={classes.sendBtn} disabled={isProcessing || (!inputValue.trim() && !selectedFile)}>➤</button>
                                        </form>
                                    </>
                                )}
                            </div>
                        </div>

                        {!isScoping && (
                            <div className={classes.canvasPanel}>
                                {renderRightPanel()}
                            </div>
                        )}

                        {showLoginPopup && (
                            <LoginPopup
                                onLoginSuccess={handleLoginSuccess}
                                onClose={() => {
                                    setShowLoginPopup(false);
                                    setPendingSubmission(null);
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Chat;